import { useEffect, useState } from "react";
import { Dropdown, DropdownButton, Table } from "react-bootstrap";
import { DateTime } from "luxon";
import FileDownload from "js-file-download";
import { Pagination } from "../../../types/PaginiationResult";
import { RoyaltyParameters } from "../../../types/Royalties/RoyaltyParameters";
import { RoyaltyOrderItemSummary } from "../../../types/Royalties/RoyaltyOrderItemSummary";
import { RoyaltyOrderItem } from "../../../types/Royalties/RoyaltyOrderItem";
import { RoyaltyReceiver } from "../../../constant/RoyaltyReceiver";
import { LayerType } from "../../../constant/LayerType";
import TableActionButton from "../../../@ui/TableActionButton";
import { TableContainer } from "../../../@ui/TableContainer";
import TableFooter from "../../Table/Footer";
import { ShrinkHeader } from "../../Table/ShrinkHeader";
import RoyaltyFilters from "./RoyaltyFilters";
import RoyaltyModal from "../../modal/Royalties/RoyaltyModal";
import { useRoyaltyService } from "../../../hooks";

const getCurrencySymbol = (layerName: string) =>
  layerName.toLowerCase().includes("inrix") ? "$" : "£";

const Royalties = () => {
  const royaltiesService = useRoyaltyService();
  const [requestParameters, setRequestParameters] = useState<RoyaltyParameters>(
    {
      page: 1,
      from: DateTime.now().startOf("quarter"),
      to: DateTime.now().startOf("quarter").plus({ months: 3 }),
    },
  );
  const [royalties, setRoyalties] =
    useState<Pagination<RoyaltyOrderItemSummary>>();
  const [selectedRoyalty, setSelectedRoyalty] = useState<RoyaltyOrderItem>();

  useEffect(() => {
    royaltiesService
      .listRoyalties(requestParameters)
      .then((data) => {
        setRoyalties(data);
      })
      .catch(() => {});
  }, [requestParameters, royaltiesService]);

  const getViewRoyaltyOrderItem = (orderItemId: string) => {
    royaltiesService
      .getRoyaltyOrderItem(orderItemId)
      .then((data) => setSelectedRoyalty(data))
      .catch(() => {});
  };

  const downloadMrn = () => {
    royaltiesService
      .getReport(requestParameters.from, requestParameters.to, LayerType.OS_MRN)
      .then(({ data, filename }) => {
        FileDownload(data, filename);
      })
      .catch(() => {});
  };

  const downloadOSHighways = () => {
    royaltiesService
      .getReport(
        requestParameters.from,
        requestParameters.to,
        LayerType.OS_HIGHWAYS,
      )
      .then(({ data, filename }) => {
        FileDownload(data, filename);
      })
      .catch(() => {});
  };

  const downloadOsPoi = () => {
    royaltiesService
      .getReport(
        requestParameters.from,
        requestParameters.to,
        LayerType.OS_POI,
      )
      .then(({ data, filename }) => {
        FileDownload(data, filename);
      })
      .catch(() => {});
  };

  const downloadInrix = () => {
    royaltiesService
      .getMultiLayerReport(requestParameters.from, requestParameters.to, [
        LayerType.INRIX_VOLUMES,
        LayerType.INRIX_TRIPS,
        LayerType.INRIX_SPEED_PROFILES,
      ])
      .then(({ data, filename }) => {
        FileDownload(data, filename);
      })
      .catch(() => {});
  };

  return (
    <>
      <RoyaltyFilters
        from={requestParameters.from}
        to={requestParameters.to}
        layer={requestParameters.layer}
        onChange={(e) =>
          setRequestParameters({
            page: 1,
            from: e.from,
            to: e.to,
            layer: e.layer,
          })
        }
      />
      <div className="mt-4 d-flex justify-content-end">
        <DropdownButton title="Download">
          <Dropdown.Item onClick={downloadMrn}>MRN</Dropdown.Item>
          <Dropdown.Item onClick={downloadOSHighways}>OSHighways</Dropdown.Item>
          <Dropdown.Item onClick={downloadOsPoi}>OS POI</Dropdown.Item>
          <Dropdown.Item onClick={downloadInrix}>INRIX</Dropdown.Item>
        </DropdownButton>
      </div>

      <hr className="my-4" />
      <TableContainer className="flex-grow mb-1">
        <Table striped bordered hover>
          <thead className="position-sticky">
            <tr>
              <th className="text-white bg-primary">Customer Name</th>
              <th className="text-white bg-primary">Order Date</th>
              <th className="text-white bg-primary">Layer</th>
              <th className="text-white bg-primary">
                Order Item Value (ex VAT)
              </th>
              <th className="text-white bg-primary">Royalty Value</th>
              <th className="text-white bg-primary">Recipient</th>
              <ShrinkHeader className="text-white bg-primary" />
            </tr>
          </thead>
          <tbody>
            {royalties?.records.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No royalties found for the selected filters.
                </td>
              </tr>
            )}
            {royalties?.records.map((royalty) => (
              <tr key={royalty.orderItemId}>
                <td>{royalty.organisationName}</td>
                <td>{royalty.orderDate.toFormat("dd/MM/yyyy")}</td>
                <td>
                  {royalty.layerName}
                  {royalty.featureType !== null &&
                    ` (${royalty.featureType
                      ?.toString()
                      .replace(/([a-z])([A-Z])/g, "$1 $2")})`}
                </td>
                <td>{getCurrencySymbol(royalty.layerName)}{royalty.orderItemValue}</td>
                <td>{getCurrencySymbol(royalty.layerName)}{royalty.royaltyValue.toFixed(2)}</td>
                <td>{RoyaltyReceiver[royalty.royaltyReceiver]}</td>
                <td>
                  <TableActionButton
                    onClick={() =>
                      getViewRoyaltyOrderItem(royalty.orderItemId)
                    }>
                    View
                  </TableActionButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <TableFooter
        currentPage={requestParameters.page}
        setCurrentPage={(page) =>
          setRequestParameters({
            page,
            from: requestParameters.from,
            to: requestParameters.to,
            layer: requestParameters.layer,
          })
        }
        totalItems={(royalties?.totalPages ?? 0) * 10}
        pageSize={10}
      />

      {selectedRoyalty && (
        <RoyaltyModal
          selectedRoyalty={selectedRoyalty}
          onClose={() => setSelectedRoyalty(undefined)}
        />
      )}
    </>
  );
};

export default Royalties;
